import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { emptyStateIconClass } from '../../modules/dashboard/themeHelpers';
import { Button } from './Button';

export interface EmptyStateAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title?: string;
  description: string;
  action?: EmptyStateAction;
  variant?: 'default' | 'compact';
  className?: string;
}

const DefaultIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  variant = 'default',
  className,
}) => {
  const { colors } = useTheme();
  const isCompact = variant === 'compact';
  const resolvedTitle = title === undefined ? 'No data' : title;

  return (
    <div
      className={cn(
        isCompact ? 'py-6 text-center' : cn(colors.surface, 'rounded-xl shadow-sm border p-8', colors.border),
        className,
      )}
    >
      <div className="text-center">
        {icon ?? <DefaultIcon className={cn(isCompact ? 'w-8 h-8 mx-auto mb-2 opacity-40' : emptyStateIconClass(colors))} />}
        {!isCompact && resolvedTitle ? (
          <h3 className={cn('mt-2 text-sm font-medium', colors.text)}>{resolvedTitle}</h3>
        ) : null}
        <p className={cn(isCompact ? 'text-xs' : 'mt-1 text-sm', colors.textMuted)}>{description}</p>
        {action ? (
          <div className={cn(isCompact ? 'mt-2' : 'mt-4')}>
            {action.href ? (
              <Link to={action.href}>
                <Button variant="default" size={isCompact ? 'sm' : 'default'}>
                  {action.label}
                </Button>
              </Link>
            ) : (
              <Button variant="default" size={isCompact ? 'sm' : 'default'} onClick={action.onClick}>
                {action.label}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmptyState;
